/**
 * Single source of truth cho trạng thái "trỏ Page" khi build Product.
 *
 * ⚠️ ĐÂY CHỈ LÀ TRẠNG THÁI TRỎ PAGE — KHÔNG remove/comment code Page gốc.
 *    Mỗi điểm render chỉ thêm 1 điều kiện:
 *        isBuilt(path) ? <PageGốc/> : <ComingSoonPage/>
 *    Gỡ path khỏi danh sách dưới đây là Page gốc hiện lại nguyên vẹn, không cần restore.
 *
 * Cách dùng khi build Product:
 *   - Page đang dev-dở / chưa làm  → thêm vào PAGE_UNBUILD với status 'to-build'.
 *   - Page đã làm xong, đang QA     → để status 'to-test'.
 *   - Page đã test OK, mở thật      → XÓA dòng tương ứng (mặc định không nằm list = build).
 *
 * `match` chấp nhận:
 *   - Path đầy đủ:  '/report/inputs/exposure'
 *   - Prefix module: '/sales/control'  → che cả nhánh con ('/sales/control/...').
 */

export type UnbuildStatus =
  | 'to-build' // dev chưa làm / chưa build luôn
  | 'to-test' // đã làm xong, đang test để chuyển qua build Product

export interface UnbuildRule {
  match: string
  status: UnbuildStatus
}

/**
 * Sub-nav đang Coming Soon — liệt kê TỪNG sub-nav riêng (giống FACTORY_TAB_UNBUILD)
 * để bật/tắt độc lập từng cái. `match` vẫn nhận prefix nếu muốn che cả nhánh.
 */
export const PAGE_UNBUILD: UnbuildRule[] = [
  // Report — Inputs
  { match: '/report/inputs/exposure', status: 'to-test' },
  { match: '/report/inputs/cashflow', status: 'to-test' },

  // Sales — Control
  { match: '/sales/control/sales-contract', status: 'to-test' },
  { match: '/sales/control/sales-allocation', status: 'to-test' },
  { match: '/sales/control/delivery-order', status: 'to-test' },
  { match: '/sales/control/receive-order', status: 'to-test' },
  { match: '/sales/control/invoice', status: 'to-test' },
  { match: '/sales/control/claim', status: 'to-test' },
  { match: '/sales/control/credit-note', status: 'to-test' },
  { match: '/sales/control/debit-note', status: 'to-test' },
  { match: '/sales/control/moving', status: 'to-test' },
  { match: '/sales/control/references', status: 'to-test' },

  // Purchase — Control / Procurement
  { match: '/procurement/purchase', status: 'to-build' },
  { match: '/procurement/contract-detail', status: 'to-build' },
  { match: '/procurement/pay-data', status: 'to-build' },
  { match: '/procurement/invoice', status: 'to-build' },
  { match: '/procurement/good-allocation', status: 'to-build' },
  { match: '/procurement/references', status: 'to-build' },
  { match: '/procurement/cvbb', status: 'to-build' },

  // Quality Control
  { match: '/quality/overview', status: 'to-test' },
  { match: '/quality/sample-data', status: 'to-test' },
  { match: '/quality/pss-label', status: 'to-test' },
  { match: '/quality/nestle-sample', status: 'to-test' },

  // Master Data
  { match: '/master-data/futures-control/calendar', status: 'to-build' },
  { match: '/master-data/futures-control/month', status: 'to-build' },
  { match: '/master-data/futures-control/holiday', status: 'to-build' },
  { match: '/master-data/items/cp-list', status: 'to-build' },
  { match: '/master-data/items/items-valuation', status: 'to-build' },
  { match: '/master-data/items/other', status: 'to-build' },

  // Virtual Warehouse
  { match: '/virtual-warehouse/received', status: 'to-build' },
  { match: '/virtual-warehouse/quality-discount', status: 'to-build' },
  { match: '/virtual-warehouse/delivery-note-print', status: 'to-build' },
  { match: '/virtual-warehouse/receive-note-print', status: 'to-build' },
]

/**
 * Tab factory đang Coming Soon — match theo TAB KEY (áp cho mọi factory code:
 * baoloc / nhontrach / nguyencuong / nongsan / pacorini). Tab `summary` GIỮ gốc.
 *
 * Có thể match sâu hơn 1 cấp `tabKey/selectorValue` (vd hub `data`) để override
 * status cho 1 sub-page cụ thể — resolver ưu tiên key kết hợp trước, rồi tới tab key.
 */
export const FACTORY_TAB_DEFAULT_STATUS: UnbuildStatus = 'to-test'

export const FACTORY_TAB_UNBUILD: UnbuildRule[] = [
  { match: 'stack', status: 'to-test' },
  { match: 'receive', status: 'to-test' },
  { match: 'issue', status: 'to-test' },
  { match: 'po-list', status: 'to-test' },
  { match: 'delivery', status: 'to-test' },
  { match: 'consumables', status: 'to-test' },
  { match: 'lot-allocation', status: 'to-test' },
  { match: 'lot-mgt', status: 'to-test' },
  { match: 'quality-standard', status: 'to-test' },
  { match: 'deduction-rate', status: 'to-test' },
  // Hub DATA — giữ trang hub (lưới card) hiển thị; Coming Soon TỪNG card:
  { match: 'data/item-list', status: 'to-test' },
  { match: 'data/bom', status: 'to-test' },
  { match: 'data/bom-cost', status: 'to-test' },
  { match: 'data/category-material', status: 'to-build' },
  { match: 'data/movement-holidays', status: 'to-build' },
  { match: 'data/movement-kpis', status: 'to-build' },
  { match: 'data/matching-scontract', status: 'to-test' },
  { match: 'data/matching-pcontract', status: 'to-test' },
  // Hub REPORT — giữ trang hub hiển thị; Coming Soon TỪNG card:
  { match: 'report/report-base-goal-real', status: 'to-test' },
  { match: 'report/report-activities', status: 'to-test' },
  { match: 'report/batch-report', status: 'to-test' },
  { match: 'report/batch-comparison', status: 'to-test' },
  { match: 'report/batch-dashboard', status: 'to-test' },
  { match: 'report/batch-analysis', status: 'to-test' },
  { match: 'report/batch-batch-analysis', status: 'to-test' },
  { match: 'internalmoving', status: 'to-test' },
  { match: 'scale-data', status: 'to-test' },
  { match: 'weight-data', status: 'to-test' },
]

/**
 * Các status đang được "MỞ" — show Page gốc DÙ vẫn nằm trong danh sách Coming Soon.
 * Bật/tắt theo lệnh của chị Đẹp, KHÔNG xóa entry trong PAGE_UNBUILD / FACTORY_TAB_UNBUILD
 * (giữ nguyên log status để lật lại bất cứ lúc nào).
 *
 *   - 'to-test' đang ở đây  → mọi mục to-test show Page gốc.
 *   - Lệnh "chuyển show Page gốc cho status X"     → thêm X vào mảng này.
 *   - Lệnh "chuyển show Coming Soon cho status X"  → bỏ X khỏi mảng này.
 */
export const REVEALED_STATUSES: UnbuildStatus[] = ['to-test']

const isRevealed = (status: UnbuildStatus): boolean =>
  REVEALED_STATUSES.includes(status)

const hit = (path: string, m: string): boolean =>
  path === m || path.startsWith(`${m}/`)

/** Rule Coming Soon khớp với URL path (undefined nếu path là Page gốc / đang được mở). */
export const getUnbuild = (path: string): UnbuildRule | undefined => {
  const rule = PAGE_UNBUILD.find((r) => hit(path, r.match))
  return rule && !isRevealed(rule.status) ? rule : undefined
}

/** true = render Page gốc; false = render ComingSoonPage. */
export const isBuilt = (path: string): boolean => !getUnbuild(path)

/**
 * Rule Coming Soon khớp với tab factory (undefined nếu tab là Page gốc).
 * Ưu tiên key kết hợp `tabKey/selectorValue` (vd 'data/category-material')
 * trước khi fallback về tab key trần ('data').
 */
export const getFactoryTabUnbuild = (
  tabKey: string,
  selectorValue?: string,
): UnbuildRule | undefined => {
  let rule: UnbuildRule | undefined
  if (selectorValue) {
    rule = FACTORY_TAB_UNBUILD.find(
      (r) => r.match === `${tabKey}/${selectorValue}`,
    )
  }
  if (!rule) rule = FACTORY_TAB_UNBUILD.find((r) => r.match === tabKey)
  return rule && !isRevealed(rule.status) ? rule : undefined
}

/** true = render tab factory gốc; false = render ComingSoonPage. */
export const isFactoryTabBuilt = (
  tabKey: string,
  selectorValue?: string,
): boolean => !getFactoryTabUnbuild(tabKey, selectorValue)
